import { useState } from "react";
import { useSearchParams } from "react-router";
import { Landmark, Send } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, InfoHint, Skeleton } from "@ncp/ui";
import { useRequests } from "./api";
import { RequestTable } from "./RequestTable";
import { OpinionRequestDialog } from "./OpinionRequestDialog";
import { RequestReviewSheet } from "./RequestReviewSheet";
import { useProcedureTermWords } from "./terms";

/** The technical opinions asked on a file (Decree-Law, Article 20): who was asked, what they answered, and a new request. */
export function TechnicalOpinionsPanel({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  // Conditions 2.13 and 2.14: the periods and provisions a request names, from their one definition and the legal rules register.
  const terms = useProcedureTermWords();
  const [params, setParams] = useSearchParams();
  const requests = useRequests({ caseId, kind: "TechnicalOpinion" });
  const [composing, setComposing] = useState(false);
  const shown = params.get("request");
  const open = (id: string | null) => setParams((p) => { const next = new URLSearchParams(p); if (id) next.set("request", id); else next.delete("request"); return next; }, { replace: true });
  const rows = requests.data ?? [];
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-normal text-muted-foreground">{t("backoffice:requests.opinions.lead", terms)}<InfoHint>{t("backoffice:requests.opinions.hint", terms)}</InfoHint></CardTitle>
        <Button size="sm" variant="gold" onClick={() => setComposing(true)}><Send /> {t("backoffice:requests.opinions.new")}</Button>
      </CardHeader>
      <CardContent>
        {requests.isLoading ? <Skeleton className="h-24" /> : rows.length === 0
          ? <EmptyState icon={Landmark} title={t("backoffice:requests.opinions.empty")} description={t("backoffice:requests.opinions.hint", terms)} />
          : <RequestTable rows={rows} onOpen={open} />}
      </CardContent>
      <OpinionRequestDialog caseId={caseId} open={composing} onOpenChange={setComposing} onSent={(r) => open(r.id)} />
      <RequestReviewSheet id={shown} onClose={() => open(null)} />
    </Card>
  );
}
